<template>
  <div v-if="showNews && isV3Group">
    <b-button
      v-if="!hideBell"
      id="news-bell-button"
      class="newsButton mr-3 dark-grey-buttons position-relative"
      @click="openModal"
    >
      <b-icon
        icon="bell-fill"
        aria-hidden="true"
        class="top--1"
        font-scale="1.1"
        scale="1.1"
      ></b-icon>
      <span v-if="hasUnseen" class="news-dot"></span>
    </b-button>
    <b-tooltip v-if="!hideBell" target="news-bell-button" delay=400>
      {{ $t('tooltips.news') }}</b-tooltip>
    <modal-news
      :visible="modalVisible"
      :entries="entries"
      :language="userObject.language"
      :seen-before="seenBeforeOpen"
      :user-created-at="userObject.createdAt"
      @hidden="modalVisible = false"
      @feedback="openFeedback"
    />
  </div>
</template>

<script>
import GetGroupPrivilegesByGroupId from '@/graphQlQueries/queries/getGroupPrivilegesByGroupId';
import GetGroupSettings from '@/graphQlQueries/queries/getGroupSettings';
import GetChangelogEntries from '@/graphQlQueries/queries/getChangelogEntries';
import UpdateUserChangelogSeen from '@/graphQlQueries/mutations/updateUserChangelogSeen';
import { hasUnseenEntries } from '@/utils/changelogNews';

// module scope: survives component re-mounts on route changes,
// resets on full page load — "once per app session"
let autoPopupDoneThisSession = false;

export default {
  name: 'HeaderNewsButton',
  components: {
    ModalNews: () => import('@/components/modals/ModalNews.vue'),
  },
  props: {
    userObject: { type: Object, required: true },
    groupId: { type: Number, required: true },
    // v3 editor header (founder 17.10.2026): the "More" menu opens the news; the modal and auto popup stay here.
    hideBell: { type: Boolean, default: false },
  },
  data() {
    return {
      showNews: false,
      // the news are for the v3 editor only (group setting enable_vue3_editor)
      isV3Group: false,
      modalVisible: false,
      entries: [],
      localSeenAt: null,
      // "seen" timestamp from before the modal opened: entries newer than it get the NEW badge
      seenBeforeOpen: null,
    };
  },
  watch: {
    newsState() { this.announce(); },
  },
  mounted() {
    this.$root.$on('header-open-news', this.openModal);
    this.$root.$on('header-news-frage', this.announce);
    this.announce();
  },
  beforeDestroy() {
    this.$root.$off('header-open-news', this.openModal);
    this.$root.$off('header-news-frage', this.announce);
    this.$root.$emit('header-news-state', null);
  },
  computed: {
    newsState() {
      return { available: this.showNews && this.isV3Group, unseen: this.showNews && this.isV3Group && this.hasUnseen };
    },
    hasUnseen() {
      return hasUnseenEntries(
        this.entries,
        this.localSeenAt || this.userObject.changelogSeenAt,
        this.userObject.createdAt,
      );
    },
  },
  apollo: {
    getGroupPrivileges: {
      query: GetGroupPrivilegesByGroupId,
      variables() {
        return { groupId: this.groupId };
      },
      update(data) {
        // the old beamer opt-out privilege keeps working for the new bell
        const disableBeamerPriv = this.findGroupPrivilegeValue('disable_beamer', data);
        this.showNews = (disableBeamerPriv !== true);
      },
    },
    getGroupSettings: {
      query: GetGroupSettings,
      variables() {
        return { groupId: this.groupId };
      },
      update(data) {
        const settings = (data.groups_by_pk && data.groups_by_pk.settings) || {};
        this.isV3Group = settings.enable_vue3_editor === true;
      },
      error() {
        // news are never critical — no bell when the setting can't be read
        this.isV3Group = false;
      },
    },
    changelog_entries: {
      query: GetChangelogEntries,
      variables() {
        // only entries written for the v3 editor; in the database that
        // generation is the audience 'editor_v2' (= enable_vue3_editor).
        // 'all' and 'editor_v1' rows date from before the v3-only changelog.
        return { limit: 5, audiences: ['editor_v2'] };
      },
      update(data) {
        this.entries = data.changelog_entries || [];
        this.maybeAutoOpen();
        return data.changelog_entries;
      },
      error() {
        // news are never critical — fail silently, bell shows without dot
        this.entries = [];
      },
      skip() {
        return !this.showNews || !this.isV3Group;
      },
    },
  },
  methods: {
    announce() {
      this.$root.$emit('header-news-state', this.newsState);
    },
    findGroupPrivilegeValue(privilegeName, data) {
      if (data.groups_by_pk) {
        const privObject = data.groups_by_pk.groups_privileges.find(
          (groupPriv) => groupPriv.privilege.handle === privilegeName,
        );
        if (privObject) {
          return privObject.value;
        }
      }
      const allGroupPrivileges = data.privileges;
      const groupPrivilege = allGroupPrivileges.find(
        (groupPriv) => groupPriv.handle === privilegeName,
      );
      if (groupPrivilege) {
        return groupPrivilege.default_value;
      }
      return null;
    },
    maybeAutoOpen() {
      if (autoPopupDoneThisSession) return;
      // don't stack the news modal on top of the shepherd dashboard tour;
      // keep the flag unconsumed so the popup can still fire later in the
      // session once the tour is done
      if (!this.$localStorage.tourOfDashboardFinished) return;
      autoPopupDoneThisSession = true;
      if (this.hasUnseen) {
        this.openModal();
      }
    },
    openModal() {
      if (!this.showNews || !this.isV3Group) return;
      this.seenBeforeOpen = this.localSeenAt || this.userObject.changelogSeenAt || null;
      this.modalVisible = true;
      this.markSeen();
    },
    openFeedback() {
      // close the news modal first so the two dialogs never stack,
      // then open ModalFeedback via the root bus (mounted in Header.vue)
      this.modalVisible = false;
      this.$nextTick(() => {
        this.$root.$emit('webmag:open-feedback');
      });
    },
    markSeen() {
      const seenAt = new Date().toISOString();
      this.localSeenAt = seenAt;
      this.$apollo.mutate({
        mutation: UpdateUserChangelogSeen,
        variables: { email: this.userObject.email, seenAt },
      }).catch(() => {
        // worst case: the popup appears once more on next login
      });
    },
  },
};
</script>

<style scoped>
.news-dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #4adc8e;
}
</style>
